import { ElementType, FC } from "react";
import styled from "styled-components";
import {
  position,
  PositionProps,
  LayoutProps,
  fontSize,
  FontSizeProps,
} from "styled-system";
import css from "@styled-system/css";

import Box from "../Box/Box";
import { BoxProps } from "../Box/box.type";
import Icon, { IconWeight } from "../Icon/Icon";

export interface LabelNumberProps extends BoxProps, PositionProps {
  /**
  Available variants: icon | number
   */
  variant?: "number" | "icon";
  /**
  A label to be displayed
   */
  label?: string;
  /**
   Font size
   */
  fontSize?: FontSizeProps["fontSize"];
  /**
  The icon name - will be applied only when variant = icon. Accepts any fontawesome icon name: see https://fontawesome.com/icons?d=gallery.
  */
  iconName?: string;
  /**
  The icon weight - will be applied only when variant = icon. It can be one of the following values: "regular" | "solid" | "light"
  */
  iconWeight?: IconWeight;
  /**
  The icon color - will be applied only when variant = icon. Accepts hex color code or pattern library collor.
  */
  iconColor?: string;
  /**
   Icon width - will be applied only when variant = icon.
   */
  iconWidth?: LayoutProps["width"];
  /**
   Icon height - will be applied only when variant = icon.
   */
  iconHeight?: LayoutProps["height"];
  /**
   The html tag to be used
   */
  as?: ElementType;
}

const Container = styled(Box)<LabelNumberProps>`
  border-radius: 50%;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  line-height: 120%;
  ${css({
    fontFamily: "normal",
    fontWeight: "bold",
  })}
  ${position}
  ${fontSize}
`;

const LabelNumber: FC<LabelNumberProps> = ({
  label,
  variant = "number",
  as = variant === "number" ? "span" : "div",
  width = "24px",
  height = "24px",
  backgroundColor = "label-number-background-color",
  color = "text-color-over-background",
  fontSize = "body",
  iconName,
  iconWeight = "regular",
  iconWidth = "20px",
  iconHeight = "20px",
  iconColor = "text-color-over-background",
  ...props
}: LabelNumberProps) => (
  <Container
    as={as}
    backgroundColor={backgroundColor}
    color={color}
    width={width}
    height={height}
    fontSize={fontSize}
    {...props}
  >
    {variant === "number" ? (
      label
    ) : (
      <Icon
        name={iconName || ""}
        width={iconWidth}
        height={iconHeight}
        weight={iconWeight}
        color={iconColor}
      />
    )}
  </Container>
);

export default LabelNumber;
